import { isValidChildAge } from './childrenAgesParam';
import type {
  HotelSearchFormErrors,
  HotelSearchFormValues,
  HotelSearchValidationResult,
  HotelValidationError,
} from '../types/hotels.presentation';

export const HOTEL_VALIDATION_CODES = {
  INVALID_DESTINATION: 'INVALID_DESTINATION',
  INVALID_CHECK_IN: 'INVALID_CHECK_IN',
  INVALID_CHECK_OUT: 'INVALID_CHECK_OUT',
  INVALID_DATE_RANGE: 'INVALID_DATE_RANGE',
  INVALID_GUESTS: 'INVALID_GUESTS',
  INVALID_ROOMS: 'INVALID_ROOMS',
  INVALID_CHILDREN_AGES: 'INVALID_CHILDREN_AGES',
  ROOMS_EXCEED_ADULTS: 'ROOMS_EXCEED_ADULTS',
} as const;

const HOTEL_FIELD_LABELS = {
  DESTINATION: 'destino',
  DATES: 'fechas',
  GUESTS: 'turistas',
  ROOMS: 'habitaciones',
} as const;

const isWholeNumber = (value: unknown) => Number.isInteger(value);

const isMissing = (value: unknown) => typeof value !== 'string' || value.trim().length === 0;

const validationError = (
  code: (typeof HOTEL_VALIDATION_CODES)[keyof typeof HOTEL_VALIDATION_CODES],
  message: string,
): HotelValidationError => ({ code, message });

export function validateHotelSearch(values: HotelSearchFormValues): HotelSearchValidationResult {
  const errors: HotelSearchFormErrors = {};

  if (
    isMissing(values.destination) ||
    isMissing(values.destinationId) ||
    isMissing(values.countryCode)
  ) {
    errors.destination = validationError(
      HOTEL_VALIDATION_CODES.INVALID_DESTINATION,
      'hotels.validation.destinationRequired',
    );
  }

  if (isMissing(values.checkIn)) {
    errors.checkIn = validationError(
      HOTEL_VALIDATION_CODES.INVALID_CHECK_IN,
      'hotels.validation.checkInRequired',
    );
  }

  if (isMissing(values.checkOut)) {
    errors.checkOut = validationError(
      HOTEL_VALIDATION_CODES.INVALID_CHECK_OUT,
      'hotels.validation.checkOutRequired',
    );
  }

  if (!isWholeNumber(values.adults) || values.adults < 1) {
    errors.adults = validationError(
      HOTEL_VALIDATION_CODES.INVALID_GUESTS,
      'hotels.validation.adultsMinimum',
    );
  }

  if (values.children !== undefined && (!isWholeNumber(values.children) || values.children < 0)) {
    errors.children = validationError(
      HOTEL_VALIDATION_CODES.INVALID_GUESTS,
      'hotels.validation.childrenNonNegativeInteger',
    );
  }

  if (values.rooms !== undefined && (!isWholeNumber(values.rooms) || values.rooms < 1)) {
    errors.rooms = validationError(
      HOTEL_VALIDATION_CODES.INVALID_ROOMS,
      'hotels.validation.roomsMinimum',
    );
  } else if (!errors.adults && (values.rooms ?? 1) > values.adults) {
    // Every room needs an adult: guests are split across rooms, never repeated per room.
    errors.rooms = validationError(
      HOTEL_VALIDATION_CODES.ROOMS_EXCEED_ADULTS,
      'hotels.validation.roomsExceedAdults',
    );
  }

  const children = values.children ?? 0;
  if (!errors.children && children > 0) {
    const ages = values.childrenAges ?? [];
    const hasEveryAge = ages.length === children && ages.every((age) => isValidChildAge(age));
    if (!hasEveryAge) {
      errors.children = validationError(
        HOTEL_VALIDATION_CODES.INVALID_CHILDREN_AGES,
        'hotels.validation.childrenAgesRequired',
      );
    }
  }

  if (!errors.checkIn && !errors.checkOut) {
    const checkIn = new Date(`${values.checkIn}T00:00:00`);
    const checkOut = new Date(`${values.checkOut}T00:00:00`);

    if (Number.isNaN(checkIn.getTime())) {
      errors.checkIn = validationError(
        HOTEL_VALIDATION_CODES.INVALID_CHECK_IN,
        'hotels.validation.checkInRequired',
      );
    }

    if (Number.isNaN(checkOut.getTime())) {
      errors.checkOut = validationError(
        HOTEL_VALIDATION_CODES.INVALID_CHECK_OUT,
        'hotels.validation.checkOutRequired',
      );
    }

    if (!errors.checkIn && !errors.checkOut && checkOut <= checkIn) {
      errors.checkOut = validationError(
        HOTEL_VALIDATION_CODES.INVALID_DATE_RANGE,
        'hotels.validation.checkOutAfterCheckIn',
      );
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
}

export function getMissingHotelFieldLabels(values: HotelSearchFormValues): string[] {
  const labels: string[] = [];
  const validation = validateHotelSearch(values);

  if (validation.errors.destination?.code === HOTEL_VALIDATION_CODES.INVALID_DESTINATION) {
    labels.push(HOTEL_FIELD_LABELS.DESTINATION);
  }

  if (
    validation.errors.checkIn?.code === HOTEL_VALIDATION_CODES.INVALID_CHECK_IN ||
    validation.errors.checkOut?.code === HOTEL_VALIDATION_CODES.INVALID_CHECK_OUT
  ) {
    labels.push(HOTEL_FIELD_LABELS.DATES);
  }

  if (
    validation.errors.adults?.code === HOTEL_VALIDATION_CODES.INVALID_GUESTS ||
    validation.errors.children?.code === HOTEL_VALIDATION_CODES.INVALID_GUESTS
  ) {
    labels.push(HOTEL_FIELD_LABELS.GUESTS);
  }

  if (validation.errors.rooms?.code === HOTEL_VALIDATION_CODES.INVALID_ROOMS) {
    labels.push(HOTEL_FIELD_LABELS.ROOMS);
  }

  return labels;
}
